import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  createDateRange,
  getFormattedTimeStringFromPeriod,
  noBarData,
  noData,
  validLineData,
  type PerformanceFilter,
} from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import {
  getThreatDefensePerformanceEngagement,
  getThreatDefensePerformanceLineChart,
} from "../../queries/threatDefensePerformanceQueries";
import { useChartPeriodMeasure, CHART_PERIOD } from "hooks/useChartPeriodMeasure";
import {
  sharedAccountReportingHorizontalBarProps,
  sharedAccountReportingLineProps,
} from "features/Reporting/Content/shared";
import { ChartWrapper, HorizontalBarChart, LineChart } from "components/charts";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import CardReporting from "components/cards/CardReporting/CardReporting";
import styles from "./ThreatDefensePhishingCampaignDrillDownBody.module.scss";
import PeopleTable from "./PeopleTable";
import { ThreatDefenseEmailStatusEnum } from "../types";

const activityColorScale = [
  reportingColors.blue,
  reportingColors.greenLight,
  reportingColors.yellow,
  reportingColors.red,
];

export type Props = {
  filter: PerformanceFilter;
  campaignId: number;
  selectedSessionId: number;
  includeAccounts?: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
};

export const ThreatDefensePhishingCampaignDrillDownBody = ({
  filter,
  campaignId,
  selectedSessionId,
  includeAccounts,
  childAccountId,
  hasCreateAccountsAddOn,
}: Props) => {
  const dateRange = useMemo(() => createDateRange(filter.dateFrom, filter.dateTo), [filter.dateFrom, filter.dateTo]);

  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    sharedAccountReportingLineProps.margins!.left + sharedAccountReportingLineProps.margins!.right,
  );

  const lineChartQuery = useQuery({
    queryFn: getThreatDefensePerformanceLineChart,
    queryKey: ["threatDefensePerformanceDrillDownLineChart", filter, chartPeriod, campaignId, selectedSessionId],
    enabled: chartPeriod !== CHART_PERIOD.UNSET,
  });

  const engagementQuery = useQuery({
    queryFn: getThreatDefensePerformanceEngagement,
    queryKey: ["threatDefensePerformanceDrillDownEngagement", filter, campaignId, selectedSessionId],
  });

  return (
    <div className={styles.root}>
      <div className={styles.activity} ref={measureRef}>
        <ChartWrapper
          titles={["Activity"]}
          showLegend
          legendLabels={[
            ThreatDefenseEmailStatusEnum.SENT,
            ThreatDefenseEmailStatusEnum.OPENED,
            ThreatDefenseEmailStatusEnum.CLICKED,
            ThreatDefenseEmailStatusEnum.SUBMITTED,
          ]}
          colorRange={activityColorScale}
        >
          <RequestStatusRenderer state={lineChartQuery.status}>
            {lineChartQuery.isSuccess && validLineData(lineChartQuery.data.lineData.yData) ? (
              <LineChart
                {...sharedAccountReportingLineProps}
                xData={lineChartQuery.data.lineData.xData}
                yData={lineChartQuery.data.lineData.yData}
                xFormatterFunc={(d) => getFormattedTimeStringFromPeriod(d, chartPeriod, dateRange)}
                colorRange={activityColorScale}
              />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.engagement}>
        <ChartWrapper titles={["Engagement"]}>
          <RequestStatusRenderer state={engagementQuery.status}>
            {engagementQuery.isSuccess && !noBarData(...engagementQuery.data.barData.map((d) => d.value)) ? (
              <HorizontalBarChart {...sharedAccountReportingHorizontalBarProps} data={engagementQuery.data.barData} />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.cards}>
        <RequestStatusRenderer state={engagementQuery.status}>
          {engagementQuery.isSuccess && <CardReporting items={engagementQuery.data.cards} />}
        </RequestStatusRenderer>
      </div>
      <div className={styles.table}>
        <h2 className={styles.sessionsHeader}>People</h2>
        <PeopleTable
          filter={filter}
          campaignId={campaignId}
          selectedSessionId={selectedSessionId}
          includeAccounts={includeAccounts}
          childAccountId={childAccountId}
          hasCreateAccountsAddOn={hasCreateAccountsAddOn}
        />
      </div>
    </div>
  );
};
